// "N of your pins nearby" block inside DetailsPanel.
//
// Closes the loop pin ↔ comune (AJTBD C8): a user who already saved a few
// listings should see them surface when they open a comune, not have to
// remember to scroll the global pins list. Two ways a pin qualifies:
//   1. exact `comuneIstat` match — preferred, always counted as "in this comune"
//   2. great-circle distance ≤ NEARBY_KM — covers pins imported before we
//      assigned comuneIstat, plus pins on the edge of an adjacent comune
//
// Render contract: silent (renders nothing) when the user has no nearby pins
// or isn't signed in — no empty state, no upsell, no noise.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { pins as pinsApi, type Pin } from '@/lib/api';
import { useSession } from '@/lib/auth-client';
import { emit } from '@/lib/events';
import { useUnitTerms } from '@/features/map-core/useUnitTerms';

const NEARBY_KM = 5;
const MAX_LIST = 5;

function distanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  // Haversine. Earth radius 6371 km.
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return 6371 * c;
}

interface PinWithDistance {
  pin: Pin;
  distance: number;
  inComune: boolean;
}

interface Props {
  istat: string;
  lat: number;
  lon: number;
}

export function PinsNearbySection({ istat, lat, lon }: Props) {
  const { t } = useTranslation();
  const terms = useUnitTerms();
  const { data: session } = useSession();

  // Reuses the same query key as PinsLayer — cache is shared so opening
  // DetailsPanel after the map already loaded pins is instant.
  const { data: allPins } = useQuery({
    queryKey: ['pins'],
    queryFn: () => pinsApi.list(),
    enabled: !!session,
    staleTime: 30 * 1000,
  });

  const matches = useMemo<PinWithDistance[]>(() => {
    if (!allPins || allPins.length === 0) return [];
    const out: PinWithDistance[] = [];
    for (const pin of allPins) {
      if (!Number.isFinite(pin.lat) || !Number.isFinite(pin.lon)) continue;
      const inComune = pin.comuneIstat === istat;
      const d = distanceKm(lat, lon, pin.lat, pin.lon);
      if (inComune || d <= NEARBY_KM) {
        out.push({ pin, distance: d, inComune });
      }
    }
    // Comune matches first, then by distance ascending.
    out.sort((a, b) => {
      if (a.inComune !== b.inComune) return a.inComune ? -1 : 1;
      return a.distance - b.distance;
    });
    return out;
  }, [allPins, istat, lat, lon]);

  if (!session || matches.length === 0) return null;

  const visible = matches.slice(0, MAX_LIST);
  const hidden = matches.length - visible.length;

  return (
    <details
      open
      className="group border-b border-slate-100 last:border-b-0"
    >
      <summary className="-mx-2 flex cursor-pointer list-none items-center gap-1.5 rounded px-2 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900">
        <span aria-hidden="true" className="shrink-0">📌</span>
        <span className="flex-1 truncate">
          {t('pins.nearbyTitle', { count: matches.length })}
        </span>
        <svg
          className="h-3 w-3 shrink-0 text-slate-400 transition-transform group-open:rotate-180"
          viewBox="0 0 12 12"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M6 8L2 4h8z" />
        </svg>
      </summary>
      <ul className="space-y-1 pb-2">
        {visible.map(({ pin, distance, inComune }) => (
          <li key={pin.id} className="flex items-center justify-between gap-2 py-1 text-xs">
            <button
              type="button"
              onClick={() => emit('places:flyToPoint', { lat: pin.lat, lon: pin.lon, zoom: 15 })}
              className="min-w-0 flex-1 truncate text-left text-slate-700 hover:text-slate-900 hover:underline"
              title={t('pins.nearbyOpen')}
            >
              {pin.title || pin.url || '—'}
            </button>
            <span className="shrink-0 font-mono text-[10px] text-slate-400">
              {inComune
                ? t('pins.nearbyInComune', terms)
                : t('pins.nearbyDistance', { km: distance.toFixed(1) })}
            </span>
          </li>
        ))}
        {hidden > 0 && (
          <li className="py-1 text-[10px] text-slate-400">
            {t('pins.nearbyMore', { count: hidden })}
          </li>
        )}
      </ul>
    </details>
  );
}
